/**
 * Celebration UI — `trigger_celebration` tool + full-widget takeover screen.
 *
 * Tool flow:
 *   1. `CelebrationToolUI` watches the tool-call status. On the
 *      running→done transition it parses the tool result and calls
 *      `show(payload)` on the celebration context.
 *   2. The Thread component renders `<CelebrationOverlay />` over its
 *      content while `active` is true. Auto-dismisses after ~15s (handled by
 *      the context) or via the close button / a CTA click.
 *
 * All visible text (headline, subtitle, bullets, CTA labels & URLs) is
 * supplied by the agent through the tool's arguments — see celebration.py.
 */

import { useEffect, useMemo, useRef, type FC } from 'react';
import { makeAssistantToolUI } from '@assistant-ui/react';
import {
  BarChart3Icon,
  InboxIcon,
  SlidersHorizontalIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import {
  useCelebration,
  type CelebrationCTA,
  type CelebrationPayload,
} from '@/client/lib/celebrationContext';

// ---------------------------------------------------------------------------
// Confetti — verbatim from the intake-ai-agent-trial prototype.
// CSS keyframes + .fullscreen-confetti class live in src/index.css.
// ---------------------------------------------------------------------------

const CONFETTI_PIECES: Array<{ left: string; delay: string; color: string }> = [
  { left: '4%',  delay: '0s',    color: '#7e55f6' },
  { left: '10%', delay: '.25s',  color: '#1F8A4C' },
  { left: '16%', delay: '.1s',   color: '#FFC857' },
  { left: '22%', delay: '.4s',   color: '#F472B6' },
  { left: '28%', delay: '.05s',  color: '#38BDF8' },
  { left: '34%', delay: '.5s',   color: '#7e55f6' },
  { left: '40%', delay: '.2s',   color: '#1F8A4C' },
  { left: '46%', delay: '.35s',  color: '#FFC857' },
  { left: '52%', delay: '.15s',  color: '#F472B6' },
  { left: '58%', delay: '.45s',  color: '#38BDF8' },
  { left: '64%', delay: '.3s',   color: '#7e55f6' },
  { left: '70%', delay: '.55s',  color: '#1F8A4C' },
  { left: '76%', delay: '.1s',   color: '#FFC857' },
  { left: '82%', delay: '.4s',   color: '#F472B6' },
  { left: '88%', delay: '.2s',   color: '#38BDF8' },
  { left: '94%', delay: '.5s',   color: '#7e55f6' },
  { left: '7%',  delay: '.7s',   color: '#1F8A4C' },
  { left: '19%', delay: '.8s',   color: '#FFC857' },
  { left: '31%', delay: '.75s',  color: '#F472B6' },
  { left: '43%', delay: '.9s',   color: '#38BDF8' },
  { left: '55%', delay: '.7s',   color: '#7e55f6' },
  { left: '67%', delay: '.85s',  color: '#1F8A4C' },
  { left: '79%', delay: '.75s',  color: '#FFC857' },
  { left: '91%', delay: '.95s',  color: '#F472B6' },
  { left: '13%', delay: '1.1s',  color: '#38BDF8' },
  { left: '37%', delay: '1.2s',  color: '#7e55f6' },
  { left: '61%', delay: '1.05s', color: '#1F8A4C' },
  { left: '85%', delay: '1.15s', color: '#FFC857' },
];

// Bullets carry no icon from the tool — cycle through three generic ones so
// the layout stays visually grounded. (Inbox / dashboard / settings.)
const BULLET_ICONS: LucideIcon[] = [InboxIcon, BarChart3Icon, SlidersHorizontalIcon];

// ---------------------------------------------------------------------------
// Overlay — full-widget takeover while celebration is active
// ---------------------------------------------------------------------------

export const CelebrationOverlay: FC = () => {
  const { active, headline, subtitle, bullets, ctas, dismiss } = useCelebration();
  if (!active) return null;

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-background">
      {/* Top bar with close */}
      <div className="flex justify-end px-3 pt-3">
        <button
          type="button"
          aria-label="Close"
          onClick={dismiss}
          className="cursor-pointer rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <XIcon className="size-4" />
        </button>
      </div>

      {/* Confetti — full viewport */}
      <div aria-hidden="true" className="fullscreen-confetti">
        {CONFETTI_PIECES.map((piece, i) => (
          <span
            key={i}
            style={{
              left: piece.left,
              animationDelay: piece.delay,
              background: piece.color,
            }}
          />
        ))}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 pb-10">
        <img src="/gaia_logo.svg" alt="Gaia" className="size-14" />

        <div className="flex flex-col items-center gap-2 text-center">
          <h2 className="text-2xl font-semibold text-foreground">{headline}</h2>
          {subtitle && (
            <p className="max-w-sm text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>

        {bullets && bullets.length > 0 && (
          <div className="flex w-full max-w-sm flex-col gap-3 rounded-2xl border bg-muted/30 p-4">
            <div className="text-sm font-semibold text-foreground">
              Here's what happens next:
            </div>
            {bullets.map((text, i) => {
              const Icon = BULLET_ICONS[i % BULLET_ICONS.length];
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-[hsl(262_60%_55%)]/10 text-[hsl(262_60%_55%)]">
                    <Icon className="size-4" />
                  </div>
                  <p className="text-sm text-foreground">{text}</p>
                </div>
              );
            })}
          </div>
        )}

        {ctas && ctas.length > 0 && (
          <div className="flex w-full max-w-sm items-center justify-center gap-2">
            {ctas.map((cta, i) => (
              <CtaButton key={i} cta={cta} onClick={dismiss} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function navigateParent(url: string) {
  window.parent.postMessage({ type: 'GAIA_EMBED_NAVIGATE', url }, '*');
}

const CtaButton: FC<{ cta: CelebrationCTA; onClick: () => void }> = ({ cta, onClick }) => {
  const handle = () => {
    navigateParent(cta.url);
    onClick();
  };

  if (cta.primary) {
    return (
      <Button
        className="h-9 flex-1 bg-foreground text-sm text-background hover:bg-foreground/90"
        onClick={handle}
      >
        {cta.label}
      </Button>
    );
  }
  return (
    <Button variant="outline" className="h-9 flex-1 text-sm" onClick={handle}>
      {cta.label}
    </Button>
  );
};

// ---------------------------------------------------------------------------
// Tool result parsing
// ---------------------------------------------------------------------------

interface TriggerCelebrationResult {
  headline?: string;
  subtitle?: string;
  bullets?: unknown;
  ctas?: unknown;
}

function parseResult(raw: unknown): TriggerCelebrationResult | null {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as TriggerCelebrationResult;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw as TriggerCelebrationResult;
  return null;
}

function coerceBullets(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((b): b is string => typeof b === 'string');
}

function coerceCtas(raw: unknown): CelebrationCTA[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
    .map((c) => ({
      label: typeof c.label === 'string' ? c.label : '',
      url: typeof c.url === 'string' ? c.url : '',
      primary: c.primary === true,
    }))
    .filter((c) => c.label && c.url);
}

function buildPayload(
  args: CelebrationArgs,
  result: TriggerCelebrationResult | null,
): CelebrationPayload | null {
  // Prefer the tool's result (canonical) but fall back to args when the
  // result hasn't arrived yet (shouldn't happen on the running→done
  // transition, but cheap to defend).
  const headline = result?.headline ?? args.headline ?? '';
  const subtitle = result?.subtitle ?? args.subtitle ?? '';
  const bullets = coerceBullets(result?.bullets ?? args.bullets);
  const ctas = coerceCtas(result?.ctas ?? args.ctas);

  if (!headline) return null;
  return { headline, subtitle, bullets, ctas };
}

// ---------------------------------------------------------------------------
// Tool UI — fires `show` once when trigger_celebration completes
// ---------------------------------------------------------------------------

interface CelebrationArgs {
  headline?: string;
  subtitle?: string;
  bullets?: unknown;
  ctas?: unknown;
}

export const CelebrationToolUI = makeAssistantToolUI<CelebrationArgs, unknown>({
  toolName: 'trigger_celebration',
  render: function Render({ args, result: rawResult, status }) {
    const { show } = useCelebration();
    const wasRunning = useRef(false);
    const hasFired = useRef(false);

    const payload = useMemo(
      () => buildPayload(args, parseResult(rawResult)),
      [args, rawResult],
    );

    useEffect(() => {
      if (status.type === 'running') {
        wasRunning.current = true;
        return;
      }
      if (
        wasRunning.current &&
        !hasFired.current &&
        status.type !== 'requires-action' &&
        payload
      ) {
        hasFired.current = true;
        wasRunning.current = false;
        show(payload);
      }
    }, [status.type, payload, show]);

    return null;
  },
});
